import { useMemo, useEffect, useState, useRef } from "react";
import { ArrowLeft, Send, Trash2, Eye, Download, FileText, MoreVertical, X, CheckCircle, AlertCircle } from "lucide-react";
import { useQuotationDraft } from "./hooks/useQuotationDraft";
import { calculateQuotationSummary } from "./utils/quotationSummary";
import { validateQuotationForm, scrollToFirstError } from "../../utils/validation";
import { showSmtpWarning } from "../../utils/smtpValidation";
import { useToast } from "../../hooks/useToast";
import { useConfirm } from "../../hooks/useConfirm";
// Reuse EXACT same components from Invoice
import InvoiceHeader from "../invoice/components/InvoiceHeader";
import BillToSectionPremium from "../invoice/components/BillToSectionPremium";
import LineItemsEditor from "../invoice/components/LineItemsEditor";
import TotalsSection from "../invoice/components/TotalsSection";
import NotesSection from "../invoice/components/NotesSection";
import LivePreview2026 from "../invoice/components/LivePreview2026";
import AutoSaveIndicator from "../invoice/components/AutoSaveIndicator";
import FullInvoicePreview from "../invoice/components/FullInvoicePreview";
import DownloadInvoiceModal from "../invoice/components/DownloadInvoiceModal";
import Toast from "../../ui/Toast";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { generateInvoicePDF } from "../../utils/pdfGenerator";
import invoiceService from "../../services/invoiceService";

// ── Friendly error messages ────────────────────────────────────────────────
function getFriendlyQuotationError(err) {
  const msg = err?.message || String(err);
  if (msg.includes('quote_number') || msg.includes('UNIQUE constraint') || msg.includes('already used by another quotation')) {
    const match = msg.match(/Quote number "([^"]+)"/);
    if (match) return `Quote number "${match[1]}" is already in use. Please change it to a unique number.`;
    return 'That quote number is already used by another quotation. Please choose a different number.';
  }
  if (msg.includes('Client is required')) return 'Please select a client before sending the quotation.';
  if (msg.includes('line item')) return 'Please add at least one line item before sending.';
  if (msg.includes("Error invoking remote method")) {
    const clean = msg.replace(/Error invoking remote method '[^']+': /, '');
    return getFriendlyQuotationError({ message: clean });
  }
  if (msg.startsWith('SqliteError:')) return 'Something went wrong saving the quotation. Please try again.';
  return msg;
}

export default function QuotationLayout2026({ quotationId = null, initialAction = null, onBack, onNavigate }) {
  const { 
    quotation, 
    updateField,
    sendQuotation,
    resendQuotation,
    deleteQuotation,
    convertToInvoice,
    saveDraft,
    isLoading,
    isSaving, 
    lastSaved,
    error,
    hasUnsavedChanges,
  } = useQuotationDraft(quotationId);

  const { toast, hideToast, success, error: showError, warning, info } = useToast();
  const { dialog, confirm } = useConfirm();

  const quotationSummary = useMemo(() => calculateQuotationSummary(quotation), [quotation]);
  const validation = useMemo(() => validateQuotationForm(quotation), [quotation]);

  const [isResending, setIsResending] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const isEditable = quotation.status === "draft";
  const isPartiallyEditable = quotation.status === "sent";
  const isReadOnly = quotation.status === "accepted" || quotation.status === "expired" || quotation.status === "converted";
  const canSend = isEditable && validation.canSend;
  const canConvert = quotation.status === "sent" || quotation.status === "accepted";

  const [showInlineValidation, setShowInlineValidation] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const headerRef = useRef(null);
  const sentinelRef = useRef(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Handle initial action
  useEffect(() => {
    if (!initialAction || isLoading) return;
    const timer = setTimeout(() => {
      switch (initialAction) {
        case 'preview': setShowFullPreview(true); break;
        case 'download': setShowDownloadModal(true); break;
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [initialAction, isLoading]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && isEditable) {
        e.preventDefault();
        handleSendQuotation();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "p") {
        e.preventDefault();
        handlePreview();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isEditable, quotation]);

  // Sticky header observer
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsHeaderSticky(!entry.isIntersecting),
      { threshold: 0, rootMargin: '0px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []);

  /* ═══════════════════════════════════════════════════════════
     ACTIONS
     ═══════════════════════════════════════════════════════════ */
  const handleBack = async () => {
    if (hasUnsavedChanges) {
      try {
        await saveDraft();
      } catch (err) {
        console.error("Failed to save before navigation:", err);
      }
    }
    if (onBack) onBack();
  };
  const handleSendQuotation = async () => {
    const canProceed = await showSmtpWarning(showError, confirm, async () => {
      try {
        await saveDraft();
      } catch (err) {
        console.error('[Quotation] Failed to save draft before navigation:', err);
      }
      if (onNavigate) onNavigate("settings");
    });
    if (!canProceed) return;

    const validation = validateQuotationForm(quotation);
    if (!validation.canSend) {
      setShowInlineValidation(true);
      scrollToFirstError(validation.errors);
      return;
    }

    const confirmed = await confirm({
      title: "Send Quotation?",
      message: "Once sent, line items and pricing will be locked. You can still update notes and metadata.",
      confirmText: "Send Quotation",
      cancelText: "Cancel",
      variant: "primary",
    });
    if (!confirmed) return;

    try {
      const result = await sendQuotation();
      if (result?.emailResult) {
        if (result.emailResult.success) {
          success(`Quotation sent successfully! Email delivered to ${quotation.client.email}`);
        } else if (result.emailResult.skipped) {
          success("Quotation sent successfully! (Email not configured)");
        } else {
          success("Quotation sent successfully!");
          warning(`Email delivery failed: ${result.emailResult.message}`);
        }
      } else {
        success("Quotation sent successfully!");
      }
    } catch (err) {
      showError(getFriendlyQuotationError(err));
    }
  };

  const handleDeleteQuotation = async () => {
    const confirmed = await confirm({
      title: "Delete Draft Quotation?",
      message: "This action cannot be undone. The quotation will be permanently deleted.",
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteQuotation();
      success("Quotation deleted successfully");
      if (onBack) onBack();
    } catch (err) {
      showError(`Failed to delete quotation: ${err.message}`);
    }
  };
  const handleResendQuotation = async () => {
    const canProceed = await showSmtpWarning(showError, confirm, async () => {
      if (onNavigate) onNavigate("settings");
    });
    if (!canProceed) return;

    const confirmed = await confirm({
      title: "Resend Quotation?",
      message: `Resend quotation ${quotation.quoteNumber || quotation.quote_number} to ${quotation.client?.email || 'the client'}?`,
      confirmText: "Resend Quotation",
      cancelText: "Cancel",
      variant: "primary",
    });
    if (!confirmed) return;

    setIsResending(true);
    try {
      const result = await resendQuotation();
      if (result?.emailResult) {
        if (result.emailResult.success) {
          success(`Quotation resent successfully! Email delivered to ${quotation.client.email}`);
        } else if (result.emailResult.skipped) {
          success("Quotation resent successfully! (Email not configured)");
        } else {
          success("Quotation resent successfully!");
          warning(`Email delivery failed: ${result.emailResult.message}`);
        }
      } else {
        success("Quotation resent successfully!");
      }
    } catch (err) {
      showError(`Failed to resend quotation: ${err.message}`);
    } finally {
      setIsResending(false);
    }
  };

  const handleConvertToInvoice = async () => {
    const confirmed = await confirm({
      title: "Convert to Invoice?",
      message: "This will create a new invoice with all quotation details. The quotation will be marked as converted.",
      confirmText: "Convert to Invoice",
      cancelText: "Cancel",
      variant: "primary",
    });
    if (!confirmed) return;

    setIsConverting(true);
    try {
      const result = await convertToInvoice();
      success(`Quotation converted to invoice ${result.invoice.invoice_number}`);
      if (onBack) onBack();
    } catch (err) {
      showError(`Failed to convert quotation: ${err.message}`);
    } finally {
      setIsConverting(false);
    }
  };

  const handlePreview = async () => {
    try {
      await saveDraft();
      setShowFullPreview(true);
    } catch (err) {
      console.error('Failed to prepare preview:', err);
      setShowFullPreview(true);
    }
  };

  const handleDownload = async () => {
    // Save draft before downloading to ensure latest data
    if (quotation.status === "draft") {
      try {
        await saveDraft();
      } catch (err) {
        console.error('Failed to save before download:', err);
      }
    }
    setShowDownloadModal(true);
  };

  const handleDownloadWithOptions = async () => {
    try {
      info("Generating PDF...");
      
      // Get auth tokens
      const authToken = sessionStorage.getItem('sessionToken');
      const deviceToken = sessionStorage.getItem('deviceToken');
      
      if (!authToken || !deviceToken) {
        showError('Authentication required. Please refresh the page.');
        return;
      }
      
      // Fetch company settings and theme
      const settingsService = await import('../../services/settings.service');
      const theme = await settingsService.default.getActiveTheme();
      const companySettings = theme?.company || theme;
      
      // Prepare quotation data with proper field mapping
      const quotationData = {
        ...quotation,
        // Ensure client data is properly structured
        client: {
          name: quotation.client?.name || quotation.client_name || '—',
          email: quotation.client?.email || quotation.client_email || '',
          phone: quotation.client?.phone || quotation.client_phone || '',
          gstin: quotation.client?.gstin || quotation.client_gstin || '',
          billing_address: quotation.client?.billing_address || quotation.client_billing_address || '',
          city: quotation.client?.city || quotation.client_city || '',
          state: quotation.client?.state || quotation.client_state || '',
          pincode: quotation.client?.pincode || quotation.client_pincode || ''
        },
        // Ensure proper field mapping
        quoteNumber: quotation.quoteNumber || quotation.quote_number,
        quoteDate: quotation.quoteDate || quotation.quote_date,
        validUntil: quotation.validUntil || quotation.valid_until,
        items: quotation.items || [],
        total: quotation.total || 0,
        subtotal: quotation.subtotal || 0,
        tax_total: quotation.tax_total || 0,
        discount: quotation.discount || 0,
        shipping: quotation.shipping || 0,
        round_off: quotation.round_off || quotation.roundOff || 0,
        notes: quotation.notes || ''
      };
      
      // Call backend to generate PDF (same as email)
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${API_URL}/api/email/generate-quotation-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'X-Device-Token': deviceToken
        },
        body: JSON.stringify({
          quotationData,
          companySettings
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to generate PDF');
      }
      
      // Get PDF blob
      const pdfBlob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quotation_${quotation.quoteNumber || quotation.quote_number || 'DRAFT'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      success("PDF downloaded successfully!");
    } catch (err) {
      showError(`Failed to download quotation: ${err.message}`);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     RENDER - 2026 UX REDESIGN - IDENTICAL TO INVOICE
     ═══════════════════════════════════════════════════════════ */
  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-50 to-slate-100/40">
        <div ref={sentinelRef} className="h-px" aria-hidden="true" />

        {/* ═══ HEADER - Simplified Action Hierarchy ═══ */}
        <div 
          ref={headerRef}
          className={`sticky top-0 z-20 transition-all duration-200 ${
            isHeaderSticky 
              ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200' 
              : 'bg-white border-b border-slate-200'
          }`}
        >
          <div className={`px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all ${
            isHeaderSticky ? 'py-2.5' : 'py-3'
          }`}>
            {/* Left: Context */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {onBack && (
                <button
                  onClick={handleBack}
                  disabled={isSaving}
                  className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Back to list"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-semibold text-slate-900 truncate">
                  {quotation.quoteNumber || "QUO-0001"}
                </span>
                <StatusBadge status={quotation.status} />
              </div>
              <div className="hidden lg:block">
                <AutoSaveIndicator lastSaved={lastSaved} isSaving={isSaving} error={error} />
              </div>
            </div>
            
            {/* Right: Clear Action Hierarchy */}
            <div className="hidden lg:flex items-center gap-2">
              {/* Secondary: Preview (Ghost) */}
              <button
                onClick={handlePreview}
                className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Eye size={14} className="inline mr-1.5" />
                Preview
              </button>

              {/* Secondary: Download PDF (Ghost) - Available in ALL states */}
              <button
                onClick={handleDownload}
                className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                title="Download quotation as PDF"
              >
                <Download size={14} className="inline mr-1.5" />
                Download PDF
              </button>

              {/* Tertiary: More Actions (Dropdown) */}
              {(quotation.status === "sent" || quotation.status === "accepted" || quotation.status === "draft") && (
                <MoreActionsMenu
                  quotation={quotation}
                  onResend={handleResendQuotation}
                  onDelete={handleDeleteQuotation}
                  onConvert={handleConvertToInvoice}
                  isResending={isResending}
                  isConverting={isConverting}
                  canConvert={canConvert}
                />
              )}
              
              {/* Primary: Send Quotation (Dominant) - For DRAFT */}
              {quotation.status === "draft" && (
                <button 
                  onClick={handleSendQuotation}
                  disabled={isLoading || isSaving || !canSend}
                  className={`px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                    canSend && !isLoading && !isSaving ? 'hover:shadow-md hover:scale-[1.02]' : ''
                  }`}
                  title={!canSend ? (validation.errors.client ? "Add a client first" : "Add line items first") : "Send quotation to client"}
                >
                  <Send size={14} className="inline mr-1.5" />
                  {isLoading ? "Sending..." : "Send Quotation"}
                </button>
              )}

              {/* Primary: Convert to Invoice (Dominant) - For SENT/ACCEPTED */}
              {canConvert && !quotation.convertedInvoiceId && (
                <div className="relative group">
                  <button
                    onClick={handleConvertToInvoice}
                    disabled={isConverting}
                    className={`px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-sm disabled:opacity-50 ${
                      !isConverting ? 'hover:shadow-md hover:scale-[1.02]' : ''
                    }`}
                    title="Convert this quotation into an invoice"
                  >
                    <FileText size={14} className="inline mr-1.5" />
                    {isConverting ? "Converting..." : "Convert to Invoice"}
                  </button>
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                    Convert this quotation into an invoice
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-slate-900"></div>
                  </div>
                </div>
              )}

              {/* Secondary: Resend - For SENT/ACCEPTED (only show if not showing Convert) */}
              {(quotation.status === "sent" || quotation.status === "accepted") && quotation.convertedInvoiceId && (
                <button
                  onClick={handleResendQuotation}
                  disabled={isResending}
                  className="px-4 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Send size={14} className="inline mr-1.5" />
                  {isResending ? "Resending..." : "Resend"}
                </button>
              )}
            </div>

            {/* Mobile: Menu */}
            <div className="lg:hidden">
              <button
                onClick={() => setShowMobileMenu(true)}
                className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <MoreVertical size={20} />
              </button>
            </div>
          </div>
        </div>
        {/* Mobile Menu */}
        {showMobileMenu && (
          <MobileMenu
            quotation={quotation}
            onClose={() => setShowMobileMenu(false)}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onResend={handleResendQuotation}
            onDelete={handleDeleteQuotation}
            onConvert={handleConvertToInvoice}
            isResending={isResending}
            isConverting={isConverting}
            canConvert={canConvert}
          />
        )}

        {/* ═══ MAIN CONTENT - Two Column Layout ═══ */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-5 px-4 sm:px-6 lg:px-8 py-4 lg:py-5">
          
          {/* LEFT: Quotation Editor - Grouped & Progressive */}
          <div className="flex-1 lg:max-w-3xl space-y-3.5">
            
            {/* Read-only / Partially editable notices */}
            {isReadOnly && (
              <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
                This quotation is {quotation.status} and cannot be edited.
              </div>
            )}
            {isPartiallyEditable && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3.5 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} className="flex-shrink-0 mt-0.5 text-blue-600" />
                  <div className="text-blue-800 space-y-0.5">
                    <div className="font-semibold">Quotation sent successfully.</div>
                    <div className="text-blue-700">Line items and pricing are now locked.</div>
                  </div>
                </div>
              </div>
            )}

            {/* Auto-save (mobile) */}
            <div className="lg:hidden">
              <AutoSaveIndicator lastSaved={lastSaved} isSaving={isSaving} error={error} />
            </div>

            {/* ═══ STEP 1: Quotation Details ═══ */}
            <InvoiceCard title="Quotation Details" step="1">
              <QuotationHeader quotation={quotation} updateField={updateField} disabled={isReadOnly} />
            </InvoiceCard>

            {/* ═══ STEP 2: Client Info ═══ */}
            <InvoiceCard title="Bill To" step="2" error={showInlineValidation && !quotation.client}>
              <BillToSectionPremium
                invoice={quotation}
                updateField={updateField}
                disabled={isReadOnly}
                showValidation={showInlineValidation}
                type="quotation"
              />
            </InvoiceCard>

            {/* ═══ STEP 3: Line Items ═══ */}
            <div className={isPartiallyEditable ? "opacity-50 pointer-events-none select-none" : ""}>
              <InvoiceCard title="Line Items" step="3" error={showInlineValidation && (!quotation.items || quotation.items.length === 0)}>
                <div className={isPartiallyEditable ? "cursor-not-allowed bg-slate-50/50" : ""}>
                  <LineItemsEditor
                    invoice={quotation}
                    updateField={updateField}
                    disabled={!isEditable}
                    showValidation={showInlineValidation}
                  />
                </div>
              </InvoiceCard>
            </div>

            {/* ═══ STEP 4: Totals ═══ */}
            <div className={isPartiallyEditable ? "opacity-50 pointer-events-none select-none" : ""}>
              <InvoiceCard title="Totals & Review" step="4">
                <div className={isPartiallyEditable ? "cursor-not-allowed bg-slate-50/50" : ""}>
                  <TotalsSection invoice={quotation} updateField={updateField} disabled={!isEditable} />
                </div>
              </InvoiceCard>
            </div>
            {/* Notes (Collapsed by default) - ENHANCED */}
            <details className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-[1px] transition-all duration-200">
              <summary className="px-4 py-2.5 cursor-pointer hover:bg-slate-50/70 active:bg-slate-100/50 transition-all duration-150 flex items-center justify-between list-none">
                <span className="text-sm font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">Notes & Terms</span>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-md group-hover:bg-slate-200 transition-colors">Optional</span>
                  <svg className="w-4 h-4 text-slate-400 transition-transform duration-200 group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </summary>
              <div className="px-4 pb-3.5 pt-2 border-t border-slate-100/90 bg-gradient-to-b from-slate-50/40 to-transparent">
                <NotesSection invoice={quotation} updateField={updateField} disabled={isReadOnly} />
              </div>
            </details>
          </div>

          {/* RIGHT: Live Preview - Enhanced & Sticky */}
          <div className="hidden lg:block w-96 sticky top-[4.5rem] h-[calc(100vh-6rem)] self-start">
            <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
              <LivePreview2026 
                summary={quotationSummary}
                validation={validation}
                onFullPreview={handlePreview}
                canSend={canSend}
                isEditable={isEditable}
                type="quotation"
              />
            </div>
          </div>
        </div>

        {/* Mobile: Sticky Bottom Action - ENHANCED */}
        {quotation.status === "draft" && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-lg">
            <div className="px-4 py-3">
              <button 
                onClick={handleSendQuotation}
                disabled={isLoading || isSaving || !canSend}
                className={`w-full px-4 py-3 text-base font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-[0.98] transition-all shadow-sm disabled:opacity-50 ${
                  canSend && !isLoading && !isSaving ? 'hover:shadow-md' : ''
                }`}
              >
                <Send size={18} className="inline mr-2" />
                {isLoading ? "Sending..." : "Send Quotation"}
              </button>
              {!canSend && !isLoading && (
                <p className="text-xs text-center text-slate-500 mt-2 animate-in fade-in duration-200">
                  {validation.errors.client ? "Add a client" : "Add line items"}
                </p>
              )}
            </div>
          </div>
        )}
        {/* Mobile: Convert to Invoice Button (for sent/accepted) */}
        {canConvert && !quotation.convertedInvoiceId && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-lg">
            <div className="px-4 py-3">
              <button 
                onClick={handleConvertToInvoice}
                disabled={isConverting}
                className="w-full px-4 py-3 text-base font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-[0.98] transition-all shadow-sm disabled:opacity-50"
              >
                <FileText size={18} className="inline mr-2" />
                {isConverting ? "Converting..." : "Convert to Invoice"}
              </button>
              <p className="text-xs text-center text-slate-500 mt-2">Convert this quotation into an invoice</p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showFullPreview && (
        <FullInvoicePreview 
          invoice={quotation} 
          onClose={() => setShowFullPreview(false)} 
          onDownload={handleDownload}
          type="quotation"
        />
      )}
      {showDownloadModal && (
        <DownloadInvoiceModal 
          invoice={quotation} 
          onClose={() => setShowDownloadModal(false)} 
          onDownload={handleDownloadWithOptions}
          type="quotation"
        />
      )}
      {toast && <Toast type={toast.type} message={toast.message} onClose={hideToast} duration={toast.duration} />}
      {dialog && <ConfirmDialog {...dialog} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   HELPER COMPONENTS - 2026 UX - IDENTICAL TO INVOICE
   ═══════════════════════════════════════════════════════════ */

// Status Badge - Minimal & Clear with Smooth Transitions
function StatusBadge({ status }) {
  const styles = {
    draft: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
    sent: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
    accepted: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    converted: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
    expired: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  };
  const style = styles[status] || styles.draft;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 ${style.bg} ${style.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${style.dot}`}></span>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
// Invoice Card - ENHANCED: Depth + Hover Lift + Micro-interactions
function InvoiceCard({ title, step, error, children }) {
  return (
    <div className={`group relative bg-white rounded-xl overflow-hidden transition-all duration-200 ${
      error 
        ? 'shadow-md shadow-red-100/60 ring-1 ring-red-200/80' 
        : 'shadow-sm hover:shadow-lg hover:-translate-y-[2px]'
    }`}>
      {/* Top accent - appears on hover */}
      <div className={`absolute top-0 left-0 right-0 h-0.5 transition-opacity duration-200 ${
        error 
          ? 'bg-gradient-to-r from-red-400 to-red-500 opacity-100' 
          : 'bg-gradient-to-r from-indigo-400 via-indigo-500 to-indigo-600 opacity-0 group-hover:opacity-100'
      }`} />
      
      <div className="px-4 py-2.5 bg-gradient-to-b from-slate-50/90 via-slate-50/50 to-transparent border-b border-slate-100/90 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {step && (
            <div className={`relative flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold transition-all duration-200 ${
              error 
                ? 'bg-gradient-to-br from-red-500 to-red-600 text-white shadow-md shadow-red-200/60 ring-1 ring-red-300/50' 
                : 'bg-gradient-to-br from-indigo-500 via-indigo-600 to-indigo-700 text-white shadow-md shadow-indigo-200/70 ring-1 ring-indigo-400/30 group-hover:shadow-lg group-hover:shadow-indigo-200/80'
            }`}>
              <span className="relative z-10">{step}</span>
              {/* Inner glow */}
              <div className="absolute inset-0.5 rounded-lg bg-white/15" />
            </div>
          )}
          <h3 className="text-sm font-bold text-slate-900 tracking-tight">{title}</h3>
        </div>
        {error && (
          <span className="text-[11px] font-semibold text-red-600 flex items-center gap-1 bg-red-50 px-2.5 py-1 rounded-md shadow-sm">
            <AlertCircle size={11} />
            Required
          </span>
        )}
      </div>
      <div className="px-4 py-3.5">
        {children}
      </div>
    </div>
  );
}

// More Actions Dropdown Menu
function MoreActionsMenu({ quotation, onResend, onDelete, onConvert, isResending, isConverting, canConvert }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
      >
        <MoreVertical size={16} />
      </button>
      
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-xl py-1 z-50">
          {canConvert && !quotation.convertedInvoiceId && (
            <button
              onClick={() => { onConvert(); setOpen(false); }}
              disabled={isConverting}
              className="w-full text-left px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FileText size={14} />
              {isConverting ? "Converting..." : "Convert to Invoice"}
            </button>
          )}
          {(quotation.status === "sent" || quotation.status === "accepted") && (
            <button
              onClick={() => { onResend(); setOpen(false); }}
              disabled={isResending}
              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Send size={14} />
              {isResending ? "Resending..." : "Resend Quotation"}
            </button>
          )}
          {quotation.status === "draft" && (
            <>
              <div className="my-1 border-t border-slate-100" />
              <button
                onClick={() => { onDelete(); setOpen(false); }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
              >
                <Trash2 size={14} />
                Delete Draft
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
// Mobile Menu
function MobileMenu({ quotation, onClose, onPreview, onDownload, onResend, onDelete, onConvert, isResending, isConverting, canConvert }) {
  return (
    <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={onClose}>
      <div 
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <span className="text-sm font-medium text-slate-900">Actions</span>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg">
            <X size={18} />
          </button>
        </div>
        <div className="p-2">
          <button
            onClick={() => { onPreview(); onClose(); }}
            className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-3"
          >
            <Eye size={18} />
            <span className="text-sm font-medium">Preview</span>
          </button>
          
          <button
            onClick={() => { onDownload(); onClose(); }}
            className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-3"
          >
            <Download size={18} />
            <span className="text-sm font-medium">Download PDF</span>
          </button>
          
          {/* Primary action for sent/accepted: Convert to Invoice */}
          {canConvert && !quotation.convertedInvoiceId && (
            <div>
              <button
                onClick={() => { onConvert(); onClose(); }}
                disabled={isConverting}
                className="w-full text-left px-4 py-3 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center gap-3 disabled:opacity-50 font-semibold"
              >
                <FileText size={18} />
                <span className="text-sm">{isConverting ? "Converting..." : "Convert to Invoice"}</span>
              </button>
              <p className="text-xs text-slate-500 mt-1 px-4">Convert this quotation into an invoice</p>
            </div>
          )}
          
          {(quotation.status === "sent" || quotation.status === "accepted") && (
            <button
              onClick={() => { onResend(); onClose(); }}
              disabled={isResending}
              className="w-full text-left px-4 py-3 text-indigo-600 hover:bg-indigo-50 rounded-lg flex items-center gap-3 disabled:opacity-50"
            >
              <Send size={18} />
              <span className="text-sm font-medium">{isResending ? "Resending..." : "Resend"}</span>
            </button>
          )}
          
          {quotation.status === "draft" && (
            <>
              <div className="my-2 border-t border-slate-100" />
              <button
                onClick={() => { onDelete(); onClose(); }}
                className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-3"
              >
                <Trash2 size={18} />
                <span className="text-sm font-medium">Delete Quotation</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Quotation Header Component - Adapts Invoice Header with quotation-specific labels
function QuotationHeader({ quotation, updateField, disabled }) {
  // Transform quotation data to match invoice structure for component reuse
  const invoiceData = {
    ...quotation,
    invoiceNumber: quotation.quoteNumber,
    issueDate: quotation.quoteDate,
    dueDate: quotation.validUntil,
  };

  const handleUpdate = (field, value) => {
    // Transform back to quotation field names
    const fieldMap = {
      invoiceNumber: 'quoteNumber',
      issueDate: 'quoteDate', 
      dueDate: 'validUntil',
    };
    const quotationField = fieldMap[field] || field;
    updateField(quotationField, value);
  };

  return (
    <InvoiceHeader 
      invoice={invoiceData}
      updateField={handleUpdate}
      disabled={disabled}
      defaultNumber="QUO-0001"
      labels={{
        title: "Quotation",
        number: "Quote Number",
        issueDate: "Quote Date",
        dueDate: "Valid Until",
        terms: "Payment terms",
      }}
    />
  );
}